import { createFileRoute } from "@tanstack/react-router";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import ContentPage from "@/components/contentPage";
import PaginationFooter from "@/components/paginationFooter";
import { titleCase } from "@/utils";
import useDebouncedQueryParams from "@/hooks/useDebouncedQueryParams";
import RealAssetCard from "@/components/cards/realAssetCard";
import { paginatedListSchema, paginatedListDefaults } from "@/schemas/paginatedListSchema";

export const Route = createFileRoute("/realassets/")({
  component: ListRealAsset,
  validateSearch: paginatedListSchema,
  loaderDeps: ({ search }) => ({ search }),
  loader: ({ context: { queryClient, api }, deps: { search } }) =>
    queryClient.prefetchQuery({
      queryKey: ["listRealAssets", ...Object.entries(search)],
      queryFn: () => api.dataAccess.listRealAssets(search),
    }),
});

function ListRealAsset() {
  const { api } = Route.useRouteContext();

  const { rawValues, setRawValues, setDebouncedQueryParams, queryParams } =
    useDebouncedQueryParams(["query"], paginatedListDefaults);

  const handleQueryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    const query = event.target.value;
    setRawValues({ query });
    setDebouncedQueryParams({
      query,
      page: 1,
    });
  };

  const { isPending, data, isPlaceholderData } = useQuery({
    queryKey: ["listRealAssets", ...Object.entries(queryParams)],
    queryFn: () => api.dataAccess.listRealAssets(queryParams),
    throwOnError: true,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  });

  if (!data || isPending) return "Loading...";

  return (
    <ContentPage title={"Real Assets"}>
      <>
        <div className="mb-4 flex w-full flex-col items-center justify-center">
          <label className="w-3/5">
            <p className="mb-2 font-light text-blue-400">
              Filter real assets by name or description...
            </p>
            <input
              onChange={handleQueryChange}
              value={rawValues.query ?? ""}
              type="text"
              className="form-input mt-1 block w-full rounded text-blue-400"
              placeholder="Nadir, Neptune, Argus, etc..."
            />
          </label>
        </div>
        <div className="grid grid-cols-1 gap-4 pt-2">
          {data.items.map((asset) => (
            <RealAssetCard
              key={asset.public_id}
              title={asset.name}
              descriptionMetaData={`Type: ${titleCase(asset.type)}`}
              description={asset.description}
              detailUrl={`/realassets/${asset.public_id}`}
            />
          ))}
        </div>
        <div className="mt-6">
          <PaginationFooter
            isPreviousData={isPlaceholderData}
            page={rawValues.page}
            setPage={(page: number) => setRawValues({ page })}
            hasMore={data.items.length !== 0}
          />
        </div>
      </>
    </ContentPage>
  );
}
